import { useState, useRef, useEffect, useCallback } from 'react'
import { X, PenTool, Eraser, Check } from 'lucide-react'

interface FirmaSempliceModalProps {
  onClose: () => void
  onConfirm: (dataUrl: string, nominativo: string) => void
  loading?: boolean
}

export default function FirmaSempliceModal({ onClose, onConfirm, loading }: FirmaSempliceModalProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [hasDrawn, setHasDrawn] = useState(false)
  const [nominativo, setNominativo] = useState('')

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Set canvas size
    const rect = canvas.getBoundingClientRect()
    canvas.width = rect.width * 2
    canvas.height = rect.height * 2
    ctx.scale(2, 2)

    // Style
    ctx.strokeStyle = '#1a1a2e'
    ctx.lineWidth = 2.5
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'

    // Background
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, rect.width, rect.height)

    // Guideline
    ctx.strokeStyle = '#e5e7eb'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(20, rect.height - 30)
    ctx.lineTo(rect.width - 20, rect.height - 30)
    ctx.stroke()

    // Reset stroke style
    ctx.strokeStyle = '#1a1a2e'
    ctx.lineWidth = 2.5
  }, [])

  const getPos = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current
    if (!canvas) return { x: 0, y: 0 }
    const rect = canvas.getBoundingClientRect()

    if ('touches' in e) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      }
    }
    return {
      x: (e as React.MouseEvent).clientX - rect.left,
      y: (e as React.MouseEvent).clientY - rect.top,
    }
  }, [])

  const startDraw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    const ctx = canvasRef.current?.getContext('2d')
    if (!ctx) return
    setIsDrawing(true)
    const { x, y } = getPos(e)
    ctx.beginPath()
    ctx.moveTo(x, y)
  }, [getPos])

  const draw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return
    e.preventDefault()
    const ctx = canvasRef.current?.getContext('2d')
    if (!ctx) return
    const { x, y } = getPos(e)
    ctx.lineTo(x, y)
    ctx.stroke()
    setHasDrawn(true)
  }, [isDrawing, getPos])

  const endDraw = useCallback(() => {
    setIsDrawing(false)
  }, [])

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const rect = canvas.getBoundingClientRect()

    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, rect.width, rect.height)

    ctx.strokeStyle = '#e5e7eb'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(20, rect.height - 30)
    ctx.lineTo(rect.width - 20, rect.height - 30)
    ctx.stroke()

    ctx.strokeStyle = '#1a1a2e'
    ctx.lineWidth = 2.5
    setHasDrawn(false)
  }, [])

  const handleConfirm = () => {
    const canvas = canvasRef.current
    if (!canvas || !hasDrawn || !nominativo.trim()) return
    const dataUrl = canvas.toDataURL('image/png')
    onConfirm(dataUrl, nominativo.trim())
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div
        className="w-full max-w-lg rounded-xl shadow-xl overflow-hidden border"
        style={{ backgroundColor: 'var(--color-bg-card)', borderColor: 'var(--color-border)' }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 border-b"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <h3 className="text-base font-semibold flex items-center gap-2">
            <PenTool className="w-5 h-5" style={{ color: 'var(--color-primary)' }} />
            Firma Semplice
          </h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg transition-colors hover:opacity-80"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          {/* Nominativo */}
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
              Nome e Cognome del firmatario *
            </label>
            <input
              type="text"
              value={nominativo}
              onChange={(e) => setNominativo(e.target.value)}
              placeholder="Es. Mario Rossi"
              className="w-full px-3 py-2 text-sm rounded-lg border outline-none transition-colors"
              style={{
                borderColor: 'var(--color-border)',
                backgroundColor: 'var(--color-bg-input, var(--color-bg-surface))',
              }}
            />
          </div>

          {/* Canvas */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                Firma qui sotto *
              </label>
              <button
                onClick={clearCanvas}
                className="flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors hover:opacity-80"
                style={{ color: 'var(--color-text-tertiary)' }}
              >
                <Eraser className="w-3 h-3" /> Cancella
              </button>
            </div>
            <div
              className="rounded-lg border overflow-hidden"
              style={{ borderColor: 'var(--color-border)' }}
            >
              <canvas
                ref={canvasRef}
                className="w-full cursor-crosshair touch-none"
                style={{ height: '180px', backgroundColor: '#fff' }}
                onMouseDown={startDraw}
                onMouseMove={draw}
                onMouseUp={endDraw}
                onMouseLeave={endDraw}
                onTouchStart={startDraw}
                onTouchMove={draw}
                onTouchEnd={endDraw}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex justify-end gap-3 px-5 py-3 border-t"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm rounded-lg transition-colors"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Annulla
          </button>
          <button
            onClick={handleConfirm}
            disabled={!hasDrawn || !nominativo.trim() || loading}
            className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg text-white transition-colors disabled:opacity-40"
            style={{ backgroundColor: 'var(--color-primary)' }}
          >
            <Check className="w-4 h-4" />
            {loading ? 'Firmando...' : 'Conferma Firma'}
          </button>
        </div>
      </div>
    </div>
  )
}
